import React, {
	createContext,
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { GlobalContext, UserContext } from "../../App";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { Game } from "../Games/Games";
import { useHistory, useLocation } from "react-router-dom";
import { SelectedGame } from "./types";
import { LevelTab } from "./types";
import Pagination from "../Pagination/Pagination";
import { getLevelData, getLevelsList, getSharedLevelsPage } from "../../api";

// page styles
import "./style.scss";
import pushHistory from "../../functions/pushHistory";
import {
	CompactLevelDisplay,
	ImageStarContainer,
} from "./LevelDisplayTypes/LevelDisplayTypes";
import ActionButton from "../Insertions/ActionButton";
import { toast } from "react-toastify";
import ToggleSwitch from "../Insertions/ToggleSwitch";
import CustomRange from "../Insertions/CustomRange";
import { LevelConstants } from "../../constants";
import downloadFile from "../../functions/downloadFile";

const LevelsContext = createContext({});

const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

const Levels = (props) => {
	const { currentUserData } = useContext<any>(UserContext);
	const { gameProperties } = useContext<any>(GlobalContext);
	const history = useHistory();
	const query = useQuery();

	const [currentTab, setCurrentTab] = useState<LevelTab | null>(null);
	const [listState, setListState] = useState<any>("default");
	const [user, setUser] = useState<any>(null);
	const [gameParamStore, setGameParamStore] = useState<any>(null);
	const [revealSearch, setRevealSearch] = useState<boolean>(false);

	// action button state sharing
	const [rotation, setRotation] = useState<number>(0);
	const stateOverrides = {
		rotation: rotation,
		setRotation: setRotation,
	};
	const [queryInputReset, setQueryInputReset] = useState<boolean>(false);
	const [fullReset, setFullReset] = useState<boolean>(false);

	// level list parameters
	const [reveal, setReveal] = useState<boolean>(false);
	const [hasDoneQuery, setHasDoneQuery] = useState<boolean>(false);
	const [isQuerying, setIsQuerying] = useState<boolean>(false);
	const [levels, setLevels] = useState<any[]>([]);
	const [numberOfPages, setNumberOfPages] = useState<number>(0);
	const [levelMessage, setLevelMessage] = useState<any>(null);
	const [previousPage, setPreviousPage] = useState<number>(-1);

	// query parameters
	const [random, setRandom] = useState<boolean>(false);
	const page = parseInt(query.get("page") ?? "1");
	const search = query.get("search") ?? "";
	const tags = query.get("tags") ?? "";
	const minStars =
		parseFloat(
			query.get("minStars") === "1" ? "" : (query.get("minStars") ?? ""),
		) || "";
	const maxStars =
		parseFloat(
			query.get("maxStars") === "5" ? "" : (query.get("maxStars") ?? ""),
		) || "";
	const greenStars = parseInt(query.get("greenStars") ?? "0") || "";
	const inverse = parseInt(query.get("inverse") ?? "0") || "";
	const unplayed = parseInt(query.get("unplayed") ?? "0") || "";
	const sort =
		query.get("sort") === LevelConstants.SortTypes.UPLOADED
			? ""
			: (query.get("sort") ?? "");

	const allQueries = useMemo(
		() => ({
			search,
			tags,
			minStars,
			maxStars,
			greenStars,
			inverse,
			sort,
			unplayed,
		}),
		[search, tags, minStars, maxStars, greenStars, inverse, sort, unplayed],
	);

	const hasSearchQuery = useMemo(() => {
		if (random) return true;
		if (page && page !== 1) return true;

		return Object.keys(allQueries).some((key) => !!allQueries[key]);
	}, [page, random, allQueries]);

	const resetButton = (
		<ActionButton
			tooltip="Reset level list [Shift + Delete/Backspace]"
			icon="refresh"
			disabled={isQuerying}
			show={hasSearchQuery}
			rotateMode={3}
			colour="rgb(255, 38, 0)"
			hoverColour="rgb(202, 0, 0)"
			toolTipPlacement={"right"}
			hideAfter={1200}
			onClick={(e) => {
				e.preventDefault(); // ESSENTIAL TO PREVENT DUPLICATE REQUESTS
				reset((resetQuery) => {
					setRevealSearch(false);
					setQueryInputReset(true);
					setRandom(false);
					let newQuery: string = "";
					if (resetQuery.get("featured") === "1")
						newQuery = "featured";
					else if (resetQuery.get("shared") === "1")
						newQuery = "shared";

					Object.keys(allQueries).forEach((key) => {
						resetQuery.delete(key);
					});
					if (newQuery) resetQuery.set(newQuery, "1");
				});
			}}
			simulatedClick={{ setState: setFullReset, constant: fullReset }}
		/>
	);

	const searchButton = (placement) => (
		<ActionButton
			tooltip={`${revealSearch ? "Close" : "Open"} search menu [Esc]`}
			icon="search"
			rotateMode={2}
			pressed={revealSearch}
			disabled={isQuerying}
			toolTipPlacement={placement}
			hoverColour="rgb(25, 0, 69)"
			onClick={(e) => {
				e.preventDefault();
				setRevealSearch((prev) => !prev);
			}}
			stateOverrides={stateOverrides}
		/>
	);

	// url parameters
	const gameParam = props?.match?.params?.game || query.get("game");
	const userParam = props?.match?.params?.id;
	const validGameParamID =
		parseInt(gameParam) >= -1 &&
		parseInt(gameParam) <= gameProperties?.length - 1;
	const validGameParamAcronym = gameProperties
		?.map((game) => game?.acronym)
		?.includes(gameParam);
	const validLevelListState =
		validGameParamID ||
		validGameParamAcronym ||
		gameParam === -1 ||
		gameParam === "all";

	const requests = useMemo(
		() => ({
			0: (searchQuery) => getLevelsList("get", searchQuery),
			1: (searchQuery) => getSharedLevelsPage(searchQuery),
			2: (searchQuery) => getLevelsList("featured", searchQuery),
			3: (searchQuery) => getLevelsList("bookmarks", searchQuery),
			4: (searchQuery) => getLevelsList("favourites", searchQuery),
		}),
		[],
	);

	const reset = useCallback(
		(call: Function | null = null) => {
			setPreviousPage(-1);
			setRandom(false);
			query.set("page", "1");
			if (call) call(query);
			setHasDoneQuery(false);
			history.push({
				search: query.toString(),
			});
		},
		[query, history],
	);

	const matchGameParam = useCallback(
		(param1, param2) => {
			// default match
			if (param1 === param2) return true;
			// all games state
			if (
				(param1 === "all" && param2 === "-1") ||
				(param1 === "all" && param2 === "all")
			)
				return true;
			const game = gameProperties.find(
				(game) => game.internalID === param1 || game.acronym === param1,
			);
			// game matches
			if (game?.internalID === param2 || game?.acronym === param2)
				return true;
			// default
			return false;
		},
		[gameProperties],
	);

	useEffect(() => {
		// properly update level list when the game url changes
		if (gameParam && !matchGameParam(gameParam, gameParamStore)) {
			setGameParamStore(gameParam);

			if (hasDoneQuery) reset();
		}
	}, [gameParam, gameParamStore, reset, matchGameParam, hasDoneQuery]);

	useEffect(() => {
		const unlisten = history.listen((location) => {
			// check if the page changed
			const newQuery = new URLSearchParams(location.search);
			const newPage = newQuery.get("page") ?? "1";
			if (parseInt(newPage) !== previousPage) setHasDoneQuery(false);
		});

		const finalSymbol = user?.username?.endsWith("s") ? "'" : "'s";
		document.title = user
			? `${user.username}${finalSymbol} levels - Level Share Square`
			: "Levels - Level Share Square";

		return () => {
			unlisten();
		};
	}, [history, page, previousPage, user]);

	useEffect(() => {
		if (
			!userParam &&
			user !== null &&
			user?._id !== userParam &&
			user?.username !== userParam
		) {
			setUser(null);
			let call: null | Function = null;
			if (query.get("shared") === "1") {
				call = (resetQuery) => {
					resetQuery.delete("shared", "1");
				};
			}
			reset(call);
		}

		if (userParam && !gameParam) {
			let newValue = "-1";
			if (currentUserData?.general_settings?.[0])
				newValue = currentUserData?.general_settings?.[0]?.toString();

			reset((resetQuery) => {
				resetQuery.set("game", newValue);
			});
		}
	}, [
		userParam,
		user?._id,
		query,
		history,
		user,
		gameParam,
		currentUserData,
		reset,
	]);

	const searchQuery = useMemo(() => {
		const queryObj = {
			page,
			name: search,
			tags,
			count: 15,
			game: gameParam || "-1",
			random: random,
			...(userParam && { author: userParam }),
			...(greenStars !== 1 && {
				minStars: minStars || 0,
				maxStars: maxStars || 5,
			}),
			authors: true,
			greenStars,
			sort,
			sortType: inverse ? 1 : -1,
			safemode: userParam ? 0 : 2,
			appendFeatured:
				currentTab && currentTab?.id === 0 && !hasSearchQuery,
			unplayed,
		};

		// Remove null or undefined keys
		Object.keys(queryObj).forEach((key) => {
			if (queryObj[key] === null || queryObj[key] === undefined) {
				delete queryObj[key];
			}
		});

		return queryObj;
	}, [
		minStars,
		maxStars,
		greenStars,
		inverse,
		sort,
		userParam,
		search,
		tags,
		gameParam,
		currentTab,
		page,
		random,
		hasSearchQuery,
		unplayed,
	]);

	useEffect(() => {
		if (window.location.pathname === "/levels") return;
		if (currentTab?.id === 1 && !searchQuery?.author) return;
		if (previousPage === page) return;
		// check if the query has already been made

		if (!hasDoneQuery && !isQuerying && currentTab !== null) {
			setIsQuerying(true);
			setReveal(false);
			// make the API call
			requests[currentTab?.id](searchQuery)
				.then((res) => {
					const data = res?.data;
					setPreviousPage(page);

					if (data) {
						setLevels(data?.levels);
						setNumberOfPages(data?.numberOfPages);
						setLevelMessage(data?.message);

						let appendedUser = null;
						if (userParam) {
							// append current user if in favourite state
							if (
								currentTab?.id === 3 &&
								(currentUserData?._id === userParam ||
									currentUserData?.username === userParam)
							)
								appendedUser = currentUserData;

							// appended user response
							if (data?.appendedUser)
								appendedUser = data?.appendedUser;

							// or level with matching author
							const levelWithAuthor = data?.levels?.find(
								(level) =>
									level?.author?._id?.toString() ===
										userParam ||
									level?.author?.username === userParam,
							);

							// set the user if either match.
							if (levelWithAuthor || appendedUser)
								setUser(
									levelWithAuthor?.author || appendedUser,
								);
						}

						if (random) {
							toast.info("Rolled a selection of random levels!", {
								position: "top-left",
							});
						}
					}
				})
				.catch((err) => {
					const data = err?.response?.data;
					if (data) {
						setLevelMessage(data?.message);
						setLevels(data?.levels);
						setNumberOfPages(0);
						setPreviousPage(-1);
						if (userParam) setUser(data?.appendedUser || null);
					}
				})
				.finally(() => {
					setHasDoneQuery(true);
					setIsQuerying(false);
					requestAnimationFrame(() => setReveal(true));
				});
		}
	}, [
		currentTab,
		page,
		hasDoneQuery,
		isQuerying,
		requests,
		previousPage,
		searchQuery,
		userParam,
		user,
		gameParam,
		random,
		currentUserData,
	]);

	if (
		!validLevelListState &&
		!window.location.pathname.includes("/levels/user/")
	)
		return (
			<div className="container no-blur">
				<GameSelect
					setCurrentUserData={props?.setCurrentUserData}
					userParam={userParam}
				/>
			</div>
		);

	return (
		<LevelsContext.Provider value={{ gameParam, userParam, reset }}>
			<div className="container mt-4">
				<div className="jumbotron">
					<ListTypeTab
						currentTab={currentTab}
						setCurrentTab={setCurrentTab}
						user={user}
						gameParamStore={gameParamStore}
						matchGameParam={matchGameParam}
						isQuerying={isQuerying}
						revealSearch={revealSearch}
					/>
					<hr />
					<LevelListHeader
						currentTab={currentTab}
						setListState={setListState}
						random={random}
						setRandom={setRandom}
						isQuerying={isQuerying}
						setRevealSearch={setRevealSearch}
						hasSearchQuery={hasSearchQuery}
						resetButton={resetButton}
						searchButton={searchButton}
						revealSearch={revealSearch}
						user={user}
					/>
				</div>
				<br />
				<div className="no-blur">
					<LevelList
						hasDoneQuery={hasDoneQuery}
						isQuerying={isQuerying}
						levels={levels}
						setLevels={setLevels}
						levelMessage={levelMessage}
						random={random}
						reveal={reveal}
						listState={listState}
						setListState={setListState}
					/>
				</div>
				<br />
				{numberOfPages > 0 && (
					<div className="MuiPagination-root no-blur">
						<Pagination
							page={page || 1}
							numberOfPages={numberOfPages || 1}
							type="comment"
							siblingCount={3}
						/>
					</div>
				)}
			</div>
			<SearchComponent
				revealSearch={revealSearch}
				setRevealSearch={setRevealSearch}
				isQuerying={isQuerying}
				resetButton={resetButton}
				searchButton={searchButton}
				queryInputReset={queryInputReset}
				setQueryInputReset={setQueryInputReset}
				setRandom={setRandom}
				random={random}
				currentTab={currentTab}
				setRotation={setRotation}
				setFullReset={setFullReset}
			/>
		</LevelsContext.Provider>
	);
};
const GameSelect = (props) => {
	const { gameProperties } = useContext(GlobalContext);
	const [selectedGame, setSelectedGame] = useState<SelectedGame | null>(null);
	const history = useHistory();
	const query = useQuery();
	const userQuery = query.get("user") ?? "";

	const handleHoverAll = () => {
		setSelectedGame({
			name: "All games",
			// total level count
			levelCount: gameProperties.reduce(
				(acc, cur) => acc + cur.levelCount,
				0,
			),
			priority: false,
			forceTail: false,
		});
	};

	return (
		<div
			style={{
				overflowX: "hidden",
				maxHeight: "calc(100vh - 200px)",
				padding: "24px",
			}}>
			<div className="game-filter-select">
				<h1 style={{ margin: "0 auto", textAlign: "center" }}>
					{selectedGame === null ? (
						"Select a category"
					) : (
						<>
							{selectedGame?.priority ? (
								<span className="wildheart-green">
									[featured]&nbsp;
								</span>
							) : selectedGame?.forceTail ? (
								<span className="gray">[archive]&nbsp;</span>
							) : (
								""
							)}
							{selectedGame?.name} -{" "}
							<span className="yellow">
								{selectedGame?.levelCount} levels
							</span>
						</>
					)}
				</h1>
				<div
					style={{
						width: "100%",
						padding: "32px",
						borderTop: "1px solid #bbb",
						overflowY: "auto",
						overflowX: "hidden",
					}}>
					<div className="game-list">
						<button
							className="game-list__button btn-primary unselectable"
							style={{
								transform: "scale(1)",
								zIndex: "0",
							}}
							onTouchStart={handleHoverAll}
							onMouseEnter={handleHoverAll}
							onMouseLeave={() => setSelectedGame(null)}
							onTouchCancel={() => setSelectedGame(null)}
							onTouchEnd={() => setSelectedGame(null)}
							onClick={() => {
								//! go to the global level list
								if (userQuery && userQuery?.length)
									return history.push(
										`/levels/user/${userQuery}?game=-1`,
									);
								history.push(`/all/levels`);
							}}>
							<h1>All games</h1>
						</button>
						{gameProperties?.map((game) => {
							const passedProps = {
								game: game,
								...props,
								setCurrentUserData: props?.setCurrentUserData,
								type: userQuery ? "levels/user" : "levels",
								author: userQuery,
								setSelectedGame: setSelectedGame,
							};
							return (
								<Fragment key={game?._id}>
									<Game {...passedProps} />
								</Fragment>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};
const ListTypeTab = ({
	currentTab,
	setCurrentTab,
	user,
	gameParamStore,
	matchGameParam,
	isQuerying,
	revealSearch,
}) => {
	const { gameParam, userParam, reset } = useContext<any>(LevelsContext);
	const { currentUserData } = useContext<any>(UserContext);
	const { gameProperties } = useContext<any>(GlobalContext);
	const query = useQuery();

	const [loaded, setLoaded] = useState<boolean>(false);
	const [prevUsername, setPrevUsername] = useState<string>("");

	const isCurrentUser = user?._id === currentUserData?._id;
	const game = gameProperties?.find(
		(game) =>
			game?.internalID === parseInt(gameParam) ||
			game?.acronym === gameParam,
	);
	const showPrivateLevelsButton =
		currentUserData?._id &&
		user !== null &&
		(isCurrentUser || currentUserData?.staffSession);

	const levelListTabs = useMemo(() => {
		const gameName = game?.name ?? "";
		const gameColor = game?.colour ?? "";
		const username = user?.username ?? "";

		const appendedUserText =
			user !== null && userParam
				? isCurrentUser
					? "your "
					: username.endsWith("s")
						? `${username}' `
						: `${username}'s `
				: "";

		const appendedGameText = game?._id ? (
			<span style={{ color: gameColor }}>{gameName} </span>
		) : null;

		const baseTabs = [
			{
				name: "All levels",
				id: 0,
				tooltip: "The default level list",
				header: (
					<>
						All {appendedUserText}
						{appendedGameText}
						levels
					</>
				),
			},
			{
				name: "Featured",
				query: "featured",
				id: 2,
				tooltip: "Currently and previously featured levels",
				header: (
					<>
						All {appendedUserText}featured {appendedGameText}levels
					</>
				),
			},
		];

		if (showPrivateLevelsButton) {
			baseTabs.splice(1, 0, {
				name: `Shared with ${isCurrentUser ? "me" : username}`,
				query: "shared",
				id: 1,
				tooltip: `Levels shared with ${isCurrentUser ? "you" : username} by other users`,
				header: (
					<>
						All {appendedGameText}levels shared with{" "}
						{isCurrentUser ? "you" : username}
					</>
				),
			});
		}

		if (currentUserData?._id && (!user || isCurrentUser))
			baseTabs.push({
				name: "My bookmarks",
				query: "bookmarked",
				id: 3,
				tooltip: `All levels you have bookmarked (private list)`,
				header: (
					<>
						All your bookmarked {appendedGameText}
						levels
					</>
				),
			});

		if (currentUserData?._id || userParam)
			baseTabs.push({
				name: "Favourited",
				query: "favourited",
				id: 4,
				tooltip: `${appendedUserText.charAt(0).toUpperCase()}${appendedUserText.slice(1) || "Your "}favourited levels (public list)`,
				header: (
					<>
						All {appendedUserText || "your "}favourited{" "}
						{appendedGameText}
						levels
					</>
				),
			});

		// indicate the tab number on the keyboard
		baseTabs.forEach((tab, index) => {
			tab.tooltip += ` [${index + 1}]`;
		});

		return baseTabs;
	}, [
		showPrivateLevelsButton,
		user,
		userParam,
		isCurrentUser,
		game?._id,
		game?.name,
		game?.colour,
		currentUserData?._id,
	]);

	const getTabIndex = useCallback(
		(tabId) => {
			let index = 0;
			index = levelListTabs.findIndex((tab) => tab?.id === tabId);
			return index;
		},
		[levelListTabs],
	);

	useEffect(() => {
		if (
			userParam !== user?._id ||
			!matchGameParam(gameParam, gameParamStore) ||
			prevUsername !== user?.username
		) {
			const index = getTabIndex(currentTab?.id);
			setPrevUsername(user?.username ?? "");
			setCurrentTab(levelListTabs[index === -1 ? 0 : index]);
		}
	}, [
		userParam,
		gameParam,
		getTabIndex,
		setCurrentTab,
		currentTab?.id,
		levelListTabs,
		game?._id,
		user,
		prevUsername,
		matchGameParam,
		gameParamStore,
	]);

	useEffect(() => {
		const keydownListener = (e) => {
			// no new requests
			if (revealSearch || isQuerying) return;
			// change tab
			if (["1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(e.key)) {
				const index = levelListTabs.findIndex(
					(_, index) => index === parseInt(e.key) - 1,
				);
				if (index === -1) return;
				setCurrentTab(levelListTabs[index]);
				reset();
			}
		};

		window.addEventListener("keydown", keydownListener);
		return () => window.removeEventListener("keydown", keydownListener);
	}, [levelListTabs, setCurrentTab, revealSearch, isQuerying, reset]);

	useEffect(() => {
		// only run once upon component render
		if (loaded) return;
		let index = getTabIndex(0);
		setLoaded(true);
		// find index for shared tab
		if (
			query.get("shared") === "1" &&
			user !== null &&
			(isCurrentUser || currentUserData?.staffSession)
		)
			index = getTabIndex(1);
		// featured tab overrides all, favourited overrides shared
		if (
			query.get("favourited") === "1" &&
			(userParam || currentUserData?._id)
		)
			index = getTabIndex(4);
		if (
			query.get("bookmarked") === "1" &&
			currentUserData?._id &&
			(!user || isCurrentUser)
		)
			index = getTabIndex(3);
		if (query.get("featured") === "1") index = getTabIndex(2);
		setPrevUsername(user?.username ?? "");
		setCurrentTab(levelListTabs[index]);
	}, [
		loaded,
		levelListTabs,
		getTabIndex,
		user,
		isCurrentUser,
		currentUserData,
		userParam,
		query,
		setCurrentTab,
	]);

	return (
		<div className="tab-container unselectable flex-tab">
			{levelListTabs.map((tab) => (
				<OverlayTrigger
					placement="top"
					key={"levels-tab-" + tab?.id}
					delay={{ show: 250, hide: 250 }}
					overlay={<Tooltip id="tab-tooltip">{tab.tooltip}</Tooltip>}>
					<button
						key={`level-tab-${tab.id}`}
						className={tab?.id === currentTab?.id ? "active" : ""}
						onClick={() =>
							!isQuerying &&
							reset((resetQuery) => {
								setCurrentTab(tab);
								// reset the tab query
								levelListTabs.forEach(
									(tab) =>
										tab?.query &&
										resetQuery.delete(tab.query),
								);
								// add the new one if applicable
								if (tab.query) resetQuery.set(tab.query, "1");
							})
						}>
						{tab.name}
					</button>
				</OverlayTrigger>
			))}
		</div>
	);
};

const LevelListHeader = ({
	currentTab,
	setListState,
	random,
	setRandom,
	isQuerying,
	revealSearch,
	setRevealSearch,
	searchButton,
	hasSearchQuery,
	resetButton,
	user,
}) => {
	const { gameParam, userParam, reset } = useContext<any>(LevelsContext);

	const [changeListState, setChangeListState] = useState<boolean>(false);
	const [changeRandom, setChangeRandom] = useState<boolean>(false);
	const history = useHistory();
	const { gameProperties } = useContext<any>(GlobalContext);
	const backButton =
		user !== null && user?._id?.length === 24 ? (
			<a
				href={`/users/${user._id}`}
				onClick={(e) => pushHistory(history, e)}>
				&nbsp;[Back]
			</a>
		) : (
			<></>
		);

	const validGame =
		gameProperties?.find(
			(game) =>
				game?.internalID === parseInt(gameParam) ||
				game?.acronym === gameParam,
		)?.forceTail !== true;
	const validTab = currentTab?.id === 0;

	const styles: any = useMemo(
		() => ({
			display: "flex",
			flexDirection: "row",
			justifyContent: "center",
			gap: "6px",
		}),
		[],
	);
	const mainButtons = useMemo(
		() => (
			<div
				style={{
					marginTop: "32px",
					...styles,
				}}>
				{validGame && validTab && (
					<a
						href={"/levels/add"}
						onClick={(e) => {
							if (e.ctrlKey) return;
							e.preventDefault();
							e.stopPropagation();
							pushHistory(history, "/levels/add", false);
						}}>
						<button className="btn btn-primary">Add a Level</button>
					</a>
				)}
				<button
					className="btn btn-primary__special"
					disabled={isQuerying}
					onClick={() =>
						pushHistory(
							history,
							!!userParam
								? `/levels?user=${userParam}`
								: "/levels",
							false,
						)
					}>
					Change game
				</button>
			</div>
		),
		[userParam, validGame, validTab, history, styles, isQuerying],
	);

	// trigger the remaining buttons/tabs
	useEffect(() => {
		const keydownListener = (e) => {
			// no new requests

			if (
				revealSearch ||
				isQuerying ||
				e.ctrlKey ||
				e.shiftKey ||
				e.altKey
			)
				return;
			if (e.key === "q") setChangeListState(true);
			if (e.key === "r") setChangeRandom(true);
		};

		window.addEventListener("keydown", keydownListener);
		return () => window.removeEventListener("keydown", keydownListener);
	});

	return (
		<>
			<h1 className="center-textoutput">
				{currentTab?.header}
				{backButton}
			</h1>
			{mainButtons}
			<div style={{ ...styles, marginTop: "6px" }}>
				{searchButton("left")}
				<ActionButton
					tooltip="Roll random levels [R]"
					icon="casino"
					disabled={isQuerying}
					show={currentTab?.id !== 1}
					pressed={random}
					rotateMode={-1}
					toolTipPlacement={"bottom"}
					hoverColour="rgb(225, 0, 255)"
					onClick={(e) => {
						e.preventDefault(); // ESSENTIAL TO PREVENT DUPLICATE REQUESTS
						setRevealSearch(false);
						reset(() => setRandom(true));
					}}
					simulatedClick={{
						setState: setChangeRandom,
						constant: changeRandom,
					}}
				/>
				<ActionButton
					disabled={isQuerying}
					icon="settings"
					toolTipPlacement={hasSearchQuery ? "bottom" : "right"}
					tooltip="Change display type [Q]"
					rotateMode={1}
					onClick={(e) => {
						e.preventDefault();
						setListState((prev) => {
							const newItem =
								prev === "default" ? "compact" : "default";
							localStorage.setItem("levelListState", newItem);
							return newItem;
						});
					}}
					simulatedClick={{
						setState: setChangeListState,
						constant: changeListState,
					}}
				/>
				{resetButton}
			</div>
		</>
	);
};

const LevelList = (props) => {
	const [loaded, setLoaded] = useState<boolean>(false);
	const setListState = props.setListState;

	useEffect(() => {
		if (loaded) return;
		const savedListState = localStorage.getItem("levelListState");

		// load saved list state from storage
		if (
			savedListState &&
			["default", "legacy", "compact"].includes(savedListState)
		)
			setListState(savedListState);
		setLoaded(true);
	}, [loaded, setListState]);

	if ((!props?.hasDoneQuery || props?.isQuerying) && !props?.levels?.length)
		return (
			<div className="d-flex justify-content-center">
				<Spinner animation="grow" variant="primary" />
			</div>
		);

	if (!props?.levels?.length)
		return (
			<div
				className="card"
				style={{
					opacity: props?.hasDoneQuery && props?.reveal ? 1 : 0,
					...((!props?.hasDoneQuery || props?.reveal) && {
						transition: "opacity 0.3s ease-in-out",
					}),
					overflow: "hidden",
				}}>
				<div className="card-body">
					<h2 className="center-textoutput">
						{props.levelMessage || "No levels found"}
					</h2>
				</div>
			</div>
		);

	return (
		<div
			className={"level-list " + props.listState}
			style={{
				opacity: props?.hasDoneQuery && props?.reveal ? 1 : 0,
				...((!props?.hasDoneQuery || props?.reveal) && {
					transition: "opacity 0.3s ease-in-out",
				}),
			}}>
			{props?.listState === "compact" && (
				<>
					<div
						className="compact-flex unselectable"
						id="compact-stats">
						<b className="wildheart-green">Level name</b>
						<b className="wildheart-green">
							{Object.values(LevelConstants.LevelStats)
								.filter((stat) => stat.default)
								.map((stat, index) => (
									<OverlayTrigger
										key={"stat-tooltip-" + index}
										placement="top"
										overlay={
											<Tooltip
												id={"stat-tooltip-" + index}>
												{stat.tooltip}
											</Tooltip>
										}>
										<span
											key={"stat-header-" + index}
											className={`material-icons-round notranslate ${stat.wide ? "wide d-lg-inline d-none" : ""}`}>
											{stat.icon}
										</span>
									</OverlayTrigger>
								))}
						</b>
					</div>
					<hr />
				</>
			)}

			{props?.levels?.map((level, index) => {
				return (
					<Fragment key={level?._id + "-" + index}>
						<MapLevels
							level={level}
							listState={props.listState}
							random={props.random}
							setLevels={props.setLevels}
						/>
					</Fragment>
				);
			})}
		</div>
	);
};

const MapLevels = ({ level, setLevels, listState, random }) => {
	const history = useHistory();
	const query = useQuery();
	const { gameParam, userParam } = useContext<any>(LevelsContext);

	const [levelData, setLevelData] = useState<any>(null);
	const [copyDataState, setCopyDataState] = useState<any>(false);

	// download/copy a level file/code upon shift click
	useEffect(() => {
		if (copyDataState) {
			setCopyDataState(false);
			const isBinary = levelData?.type === "Buffer";
			(isBinary
				? downloadFile(
						new Blob([new Uint8Array(levelData?.data)], {
							type: "application/zip",
						}),
						{ ...level, extension: levelData?.extension },
					)
				: navigator.clipboard.writeText(levelData)
			).then(() => {
				toast.success(
					isBinary ? "Downloaded level!" : "Copied level code!",
				);
				if (levelData?.success) {
					setLevelData((prev) => ({ ...prev, success: false })); // reset the success property
					setLevels(
						(
							prev, // update the hasplayed property
						) =>
							prev?.map((l) =>
								l?._id === level?._id
									? {
											...l,
											hasPlayed: true,
											plays: l?.plays + 1,
										}
									: l,
							),
					);
				}
			});
		}
	}, [copyDataState, levelData, level, setLevels]);

	const clickHandler = (e) => {
		// ctrl + shift click gets the code directly
		if (e.ctrlKey && e.shiftKey) {
			e.preventDefault();
			if (!levelData) {
				const msg = toast.loading("Getting content...");
				// api call
				getLevelData(level?._id)
					.then((res) => {
						setLevelData({
							...res?.data?.levelData,
							extension: res?.data?.extension,
							success: res?.data?.success,
						});
						setCopyDataState(true);
					})
					.finally(() => toast.dismiss(msg));
			} else setCopyDataState(true);
		}
		// let it open in new tab
		if (e.ctrlKey) return;
		e.preventDefault();
		// go to new page
		const url = `/levels/${level?._id}`;
		const extraParams = {
			...(gameParam && !query.get("game") && { game: gameParam }),
			...(userParam && { author: userParam }),
		};
		history.push(
			!random
				? url
				: `${url}?random=1&${new URLSearchParams(window.location.search)}&${new URLSearchParams(
						extraParams,
					)}`,
		);
		window.scrollTo(0, 0);
	};

	if (listState === "compact")
		return (
			<CompactLevelDisplay level={level} clickHandler={clickHandler} />
		);

	// default list is the image star container
	return (
		<ImageStarContainer
			level={level}
			hoverEvents={true}
			clickHandler={clickHandler}
			hoverCard={false}
		/>
	);
};

const SearchComponent = ({
	revealSearch,
	setRevealSearch,
	isQuerying,
	searchButton,
	resetButton,
	queryInputReset,
	setQueryInputReset,
	setRandom,
	random,
	currentTab,
	setRotation,
	setFullReset,
}) => {
	const { reset } = useContext<any>(LevelsContext);
	const query = useQuery();
	const inputRef = useRef(null);
	const [savedRandom, setSavedRandom] = useState<any>(false);
	const [triggeredQueryState, setTriggeredQueryState] = useState<any>(false);

	interface SearchParams {
		search: string | null;
		tags: string | null;
		inverse: string | null;
		sort: string | null;
		minStars: string | null;
		maxStars: string | null;
		greenStars: string | null;
		unplayed: string | null;
	}

	// Search components
	const [searchParams, setSearchParams] = useState<SearchParams>({
		search: query.get("search") ?? "",
		tags: query.get("tags") ?? "",
		inverse: query.get("inverse") ?? "",
		sort: query.get("sort") ?? "",
		minStars: query.get("minStars") ?? "1",
		maxStars: query.get("maxStars") ?? "5",
		greenStars: query.get("greenStars") ?? "0",
		unplayed: query.get("unplayed") ?? "0",
	});

	const searchParamsAreDefault = () => {
		return (
			searchParams.search === "" &&
			searchParams.tags === "" &&
			searchParams.inverse === "" &&
			searchParams.sort === "" &&
			searchParams.minStars === "1" &&
			searchParams.maxStars === "5" &&
			searchParams.greenStars === "0" &&
			searchParams.unplayed === "0" &&
			!savedRandom
		);
	};

	const executeQuery = () => {
		reset((resetQuery) => {
			Object.keys(searchParams).forEach((key) => {
				const excludeMinMax =
					resetQuery.get("greenStars") === "1" &&
					["minStars", "maxStars"].includes(key);
				if (searchParams[key] === "" || excludeMinMax) {
					resetQuery.delete(key);
				} else {
					resetQuery.set(key, searchParams[key]);
				}
			});
			setRandom(savedRandom);
		});
		if (window.innerWidth <= 992) {
			setRevealSearch(false);
		}
	};

	useEffect(() => {
		const keydownListener = (e) => {
			// open/close
			if (e.key === "Escape")
				return setRevealSearch((prev) => {
					const newState = !prev;
					if (newState) setRotation(180);
					return newState;
				});
			// clear current queries
			if (
				["Delete", "Backspace"].includes(e.key) &&
				e.ctrlKey &&
				revealSearch
			)
				return setQueryInputReset(true);

			//! only allowed when not making api call
			if (isQuerying) return;

			// reset level list
			if (["Delete", "Backspace"].includes(e.key) && e.shiftKey)
				setFullReset(true);
			// make query
			if (e.key === "Enter" && revealSearch) {
				e.preventDefault();
				return setTriggeredQueryState(true);
			}
		};

		window.addEventListener("keydown", keydownListener);

		return () => {
			window.removeEventListener("keydown", keydownListener);
		};
	}, [
		revealSearch,
		setRevealSearch,
		setRotation,
		isQuerying,
		setFullReset,
		setQueryInputReset,
	]);

	useEffect(() => {
		if (queryInputReset) {
			setSearchParams({
				search: "",
				tags: "",
				inverse: "",
				sort: "",
				minStars: "1",
				maxStars: "5",
				greenStars: "0",
				unplayed: "0",
			});
			setSavedRandom(false);
			setQueryInputReset(false);
		}
	}, [queryInputReset, setQueryInputReset]);

	useEffect(() => {
		setSavedRandom(random);
	}, [random]);

	useEffect(() => {
		const current: HTMLInputElement = inputRef.current!;
		// focus and unfocus the input field
		if (current) {
			revealSearch ? current.focus() : current.blur();
		}
	}, [revealSearch]);

	const sortTypes = LevelConstants.SortTypes;
	const sortSelectProps = {
		[sortTypes.UPLOADED]: {
			label: "Publish date",
			inverse: ["Oldest", "Newest"],
		},
		[sortTypes.FAVOURITES]: {
			label: "Favourites",
			inverse: ["Least", "Most"],
		},
		[sortTypes.RATING]: { label: "Rating", inverse: ["Lowest", "Highest"] },
		[sortTypes.PLAYS]: { label: "Plays", inverse: ["Least", "Most"] },
		[sortTypes.RATES]: { label: "Rates", inverse: ["Lowest", "Highest"] },
		[sortTypes.COMMENTERS]: {
			label: "Commenters",
			inverse: ["Least", "Most"],
		},
	};

	const hideSort = currentTab?.id === 1;
	const hideRateQueries = currentTab?.id === 1;

	return (
		<div
			className="search-list unselectable"
			style={{
				transform: revealSearch ? "translateY(0)" : "translateY(110%)",
			}}>
			<div>
				<ActionButton
					tooltip={`Launch search query [Enter]`}
					icon="rocket"
					rotateMode={-4}
					disabled={isQuerying}
					toolTipPlacement="right"
					colour="rgb(49, 224, 0)"
					hoverColour="rgb(108, 255, 110)"
					onClick={(e) => {
						e.preventDefault();
						executeQuery();
					}}
					simulatedClick={{
						setState: setTriggeredQueryState,
						constant: triggeredQueryState,
					}}
				/>
				{resetButton}
				<ActionButton
					tooltip={`Clear query [Ctrl + Delete/Backspace]`}
					icon="clear_all"
					iconType="material-icons-outlined"
					rotateMode={-3}
					disabled={isQuerying}
					show={!searchParamsAreDefault()}
					toolTipPlacement="right"
					colour="rgba(255, 111, 0, 1)"
					hoverColour="rgba(214, 54, 0, 1)"
					hideAfter={1000}
					simulatedClick={{
						setState: setQueryInputReset,
						constant: queryInputReset,
					}}
					onClick={() => {
						setQueryInputReset(true);
					}}
				/>
				{searchButton("right")}
			</div>
			{/* Filter component */}
			<div>
				{/* Search queries*/}
				<div>
					<h2>Search</h2>
					<input
						type="text"
						className="form-control"
						placeholder="Search by name, id and description"
						value={searchParams.search || ""}
						ref={inputRef}
						onChange={(e) =>
							setSearchParams((prev) => ({
								...prev,
								search: e.target.value,
							}))
						}
					/>
					<h2>
						Tags{" "}
						<OverlayTrigger
							placement="top"
							overlay={
								<Tooltip>
									TAGS ARE SUBJECT TO CHANGE IN A FUTURE SITE
									PATCH TO INCLUDE DIFFICULTY/LENGTH
								</Tooltip>
							}>
							<span className="material-icons">warning</span>
						</OverlayTrigger>
					</h2>
					<input
						type="text"
						placeholder="Search by tags, separated by commas"
						className="form-control"
						value={searchParams.tags || ""}
						onChange={(e) =>
							revealSearch &&
							setSearchParams((prev) => ({
								...prev,
								tags: e.target.value,
							}))
						}
					/>
				</div>
				{/* Sorting types */}
				<div style={{ display: hideSort ? "none" : "block" }}>
					<h2>Sort type</h2>
					{!savedRandom && (
						<>
							<select
								value={searchParams.sort || sortTypes.UPLOADED}
								onChange={(e) =>
									revealSearch &&
									setSearchParams((prev) => ({
										...prev,
										sort: e.target.value,
									}))
								}>
								{Object.values(sortTypes).map((type) => (
									<option
										key={"sortype-" + type}
										value={type}>
										{type}
									</option>
								))}
							</select>
							<ToggleSwitch
								isOn={searchParams.inverse !== "1"}
								onToggle={() => {
									setSearchParams((prev) => ({
										...prev,
										inverse:
											searchParams.inverse === "1"
												? ""
												: "1",
									}));
								}}
							/>{" "}
							{
								sortSelectProps[
									searchParams.sort || sortTypes.UPLOADED
								]?.inverse[searchParams.inverse === "1" ? 0 : 1]
							}
							<br />
						</>
					)}
					<ToggleSwitch
						isOn={savedRandom}
						onToggle={() => setSavedRandom(!savedRandom)}
					/>{" "}
					Randomize
				</div>
				{/* Star rating */}
				<div style={{ display: hideRateQueries ? "none" : "block" }}>
					{" "}
					<h2>Min/max star range</h2>
					<ToggleSwitch
						isOn={searchParams.greenStars === "1"}
						onToggle={() => {
							setSearchParams((prev) => ({
								...prev,
								greenStars:
									searchParams.greenStars === "1" ? "" : "1",
							}));
						}}
					/>{" "}
					Green stars
					<ToggleSwitch
						isOn={searchParams.unplayed === "1"}
						onToggle={() => {
							setSearchParams((prev) => ({
								...prev,
								unplayed:
									searchParams.unplayed === "1" ? "" : "1",
							}));
						}}
					/>{" "}
					Not played by me
					{searchParams.greenStars !== "1" && revealSearch ? (
						<CustomRange
							setSearchParams={setSearchParams}
							searchParams={searchParams}
							fields={["minStars", "maxStars"]}
							reset={["greenStars"]}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default Levels;
